import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from "react-i18next";
import { Gantt, ViewMode } from 'gantt-task-react';
import "gantt-task-react/dist/index.css";

import { connect } from "react-redux";
import { compose } from "redux";

import { getComplianceStandards, getComplianceActions, addComplianceActionComments, editComplianceActionComments } from "../../store/actions/complianceActions";
import {editActionProgress} from "../../store/actions/riskActions";

import debounce from "lodash/debounce";
import { Disable } from 'react-disable';

import "./Gantt.css";
import ActionOverlay from "../../components/Action/ActionOverlay";
import Popup from 'reactjs-popup';
import 'reactjs-popup/dist/index.css';

const ComplianceGantt = (props) => {
    const { complianceStandards, getComplianceActionsFN, complianceActions, addComplianceActionCommentsFN, editComplianceActionCommentsFN, activeComplianceId, profile } = props;

    const { t, i18n } = useTranslation();
    const ganttLocale = (i18n.language || "").toLowerCase().startsWith("tr") ? "tr-TR" : "en-GB";

    const TaskListHeader = useMemo(
        () =>
            function TaskListHeaderInner({ headerHeight, rowWidth, fontFamily, fontSize }) {
                return (
                    <div
                        style={{
                            fontFamily,
                            fontSize,
                            height: headerHeight,
                            width: rowWidth,
                            minHeight: headerHeight,
                            display: "flex",
                            alignItems: "center",
                            borderBottom: "1px solid rgb(196, 196, 196)",
                            paddingLeft: 10,
                            fontWeight: 600,
                            boxSizing: "border-box",
                            backgroundColor: "#fff",
                        }}
                    >
                        {t("actionsPage.gantt.nameColumn")}
                    </div>
                );
            },
        [t]
    );

    const dateTimeFormat = useCallback(
        (date, viewMode) => {
            if (viewMode === ViewMode.Day || viewMode === "Day") {
                return date.getDate().toString();
            }
            return date.toLocaleDateString(ganttLocale, { month: "short" });
        },
        [ganttLocale]
    );

    const [tasks, setTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [risk_id, setRiskId] = useState(null);
    const [isActionOpen, setIsActionOpen] = useState(false);
    const [action_id, setActionId] = useState(null);

    const updateServerData = useCallback(
        debounce((comment_iid, comments, compliance_id) => {
            setIsLoading(true);
            editComplianceActionCommentsFN(comment_iid, comments, compliance_id);
        }, 1500),
        []
    );

    useEffect(() => {
        if (activeComplianceId) {
            if (profile.role !== "User") {
                getComplianceActionsFN(activeComplianceId);
            }
        }
    }, [activeComplianceId]);

    useEffect(() => {
        setTasks([]);
        if(complianceActions){
        if (complianceActions?.length > 0) {
            let newTasks = complianceActions.map(action => ({
                ...action,
                start: new Date(action.start),
                end: new Date(action.end),
                hideChildren: true
            }));

            setTasks(newTasks);
            setIsLoading(false);
        } else {
            setTasks([]);
        }
    }
    }, [complianceActions]);

    const handleExpanderClick = (task) => {
        setTasks(tasks.map((row) => (row.id === task.id ? task : row)));
    };

    const handleAddTask = (task) => {
        addComplianceActionCommentsFN(task.iid, "New Comment", activeComplianceId);
    };

    const closeAction = () => {
        setIsActionOpen(false);
    };

    const openAction = (action) => {
        //////console.log("action open?", action)
        if(!action.iid) return
        setActionId(action.iid);
        setRiskId(action.risk_id);
        setIsActionOpen(true);
    };

    const handleProgressChange = (task, progress) => {
        if (!task.iid) {
            console.error("Task IID is missing.");
            return;
        }

        props.editActionProgressFN(task.iid, task.progress)
            .then(() => {
                setTasks((prevTasks) =>
                    prevTasks.map((row) => (row.iid === task.iid ? { ...row, progress: task.progress } : row))
                );
            })
            .catch((error) => {
                console.error("Failed to update progress:", error);
                alert("Failed to update progress. Please try again.");
            });
    };

    //////console.log("complianceActions:", complianceActions);
    return (
        <div className="w-full p-2 relative"> {/* ✅ relative keeps it in layout flow */}
            {tasks?.length > 0 ? (
                <div className="w-full overflow-x-auto">
                    <div style={{ display: "inline-flex", width: "max-content" }}>
                        <Disable disabled={isLoading}>
                            <Gantt
                                tasks={tasks}
                                onExpanderClick={handleExpanderClick}
                                rowHeight={50}
                                rowWidth={100}
                                openAction={openAction}
                                handleAddTask={handleAddTask}
                                onClick={openAction}
                                listCellWidth="300px"
                                viewDate={new Date(new Date().setDate(new Date().getDate() - 7))}
                                viewMode={ViewMode.Day}
                                locale={ganttLocale}
                                TaskListHeader={TaskListHeader}
                                dateTimeFormat={dateTimeFormat}
                                todayColor="rgba(255, 0, 0, 0.2)"
                                onProgressChange={handleProgressChange}
                                columnWidth={40}
                                fontSize="12px"
                            />
                        </Disable>
                    </div>
                </div>
            ) : (
                <div className="w-full h-50 flex justify-center items-center">
                    <span className="text-gray-500">{t("actionsPage.gantt.empty")}</span>
                </div>
            )}

            <Popup
                open={isActionOpen}
                onClose={closeAction}
                contentStyle={{
                    overflow: 'auto',
                    maxHeight: '80vh',
                    width: '900px',
                    marginTop: '14vh'
                }}
            >
                <ActionOverlay closeAction={closeAction} risk_id={risk_id} action_id={action_id} />
            </Popup>
        </div>
    );
};

const mapStateToProps = (state) => ({
    complianceStandards: state.compliance.complianceStandards,
    complianceActions: state.compliance.actions,
    profile: state.profile.profile,
});

const mapDispatchToProps = (dispatch) => ({
    getComplianceActionsFN: (compliance_id) => dispatch(getComplianceActions(compliance_id)),
    addComplianceActionCommentsFN: (action_id, comments, compliance_id) => dispatch(addComplianceActionComments(action_id, comments, compliance_id)),
    editComplianceActionCommentsFN: (comment_iid, comments, compliance_id) => dispatch(editComplianceActionComments(comment_iid, comments, compliance_id)),
    editActionProgressFN: (action_id, progress) => dispatch(editActionProgress(action_id, progress)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(ComplianceGantt);